import { CancelTokenSource } from 'axios';
import { createContext, FunctionComponent, PropsWithChildren, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { clearStashedDataSource, stashDataSource } from '../features/dataSources/dataSourcesSlice';
import { resetUploadStatus, updateFileInfo, updateUploadStatus } from '../features/fileUploading/fileUploadingSlice';
import { IFileUploadContext } from '../models/fileUpload';
import { RootState, useAppDispatch } from '../reducers';
import { useSaveDataSourceMutation, useUploadDataSourceFileMutation } from '../services/dataSources';
import { getStatus } from '../utils';
import { DataSource, DataSourceFileConfig } from '@/models/dataSource';

export const FileUploadContext = createContext<IFileUploadContext>({} as IFileUploadContext);

const FileUploadProvider: FunctionComponent<PropsWithChildren> = ({ children }) => {
  const dispatch = useAppDispatch();
  const dataSource = useSelector((state: RootState) => state.dataSources.edit?.dataSource) as Partial<DataSource<DataSourceFileConfig>> | undefined;
  const backgroundStatus = useSelector((state: RootState) => state.fileUploading.backgroundStatus);

  const [saveDataSource] = useSaveDataSourceMutation();
  const [sendFile, { isLoading, isError, isSuccess, data, error }] = useUploadDataSourceFileMutation();

  useEffect(() => {
    // TEMP
    // quando l'utente cancella l'uploading del file, useUploadDataSourceFileMutation restituisce isSuccess=true e data=undefined
    // trovare un modo intelligente per controllare se l'utente ha annullato l'operazione di caricamento

    if (isSuccess && !data) {
      dispatch(resetUploadStatus());
      return;
    }
    if (isSuccess && data) {
      dispatch(stashDataSource({ configuration: { ...dataSource?.configuration, url: data.path } }));
      if (
        backgroundStatus?.continue &&
        dataSource &&
        dataSource.name &&
        dataSource.configuration &&
        dataSource.type &&
        !!backgroundStatus.workspaceId
      ) {
        saveDataSource({
          workspaceId: backgroundStatus.workspaceId,
          dataSource: {
            type: dataSource.type.id,
            name: dataSource.name,
            configuration: { ...dataSource?.configuration, url: data.path },
          },
        });
        dispatch(resetUploadStatus());
        dispatch(clearStashedDataSource());
      }
    }

    if (isError && backgroundStatus?.continue) {
      dispatch(resetUploadStatus());
      return;
    }

    dispatch(
      updateUploadStatus({
        uploadingStatus: getStatus(isLoading, isSuccess, isError),
        data,
        error,
      })
    );
  }, [isLoading, isError, isSuccess, data, error]);

  const uploadFile = (file: File, cancelTokenSource: CancelTokenSource) => {
    const filename = file.name;
    const path = URL.createObjectURL(file);
    dispatch(updateFileInfo({ path, filename }));

    const formData = new FormData();
    formData.append('file', file);

    sendFile({ file: formData, cancelTokenSource: cancelTokenSource });
  };

  const contextValue: IFileUploadContext = {
    uploadFile: useCallback(uploadFile, []),
  };

  return <FileUploadContext.Provider value={contextValue}>{children}</FileUploadContext.Provider>;
};

export default FileUploadProvider;
